import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";

export type ThemeMode = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

interface ThemeContextValue {
  /** Raw user preference (light/dark/system). */
  mode: ThemeMode;
  /** Effective theme actually applied to the DOM (light or dark). */
  resolvedTheme: ResolvedTheme;
  /** Set explicit preference. */
  setMode: (mode: ThemeMode) => void;
  /** Toggle between light and dark (collapses 'system'). */
  toggle: () => void;
}

const STORAGE_KEY = "safersavvy:theme";

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const getSystemTheme = (): ResolvedTheme => {
  if (typeof window === "undefined") return "light";
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
};

const readStoredMode = (): ThemeMode => {
  if (typeof window === "undefined") return "system";
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    if (v === "light" || v === "dark" || v === "system") return v;
  } catch {
    /* ignore */
  }
  return "light";
};

const applyThemeClass = (resolved: ResolvedTheme, animate = true) => {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (animate) {
    root.classList.add("theme-transition");
    window.setTimeout(() => root.classList.remove("theme-transition"), 250);
  }
  if (resolved === "dark") root.classList.add("dark");
  else root.classList.remove("dark");
  // Help native form controls / scrollbars pick up the right palette.
  root.style.colorScheme = resolved;
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mode, setModeState] = useState<ThemeMode>(() => readStoredMode());
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() => {
    const stored = readStoredMode();
    return stored === "system" ? getSystemTheme() : stored;
  });

  /* Apply DOM class whenever resolvedTheme changes. */
  useEffect(() => {
    applyThemeClass(resolvedTheme);
  }, [resolvedTheme]);

  /* React to system preference changes when in 'system' mode. */
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e: MediaQueryListEvent) => {
      if (mode === "system") setResolvedTheme(e.matches ? "dark" : "light");
    };
    mql.addEventListener?.("change", handler);
    return () => mql.removeEventListener?.("change", handler);
  }, [mode]);

  /* Cross-tab sync. */
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return;
      const next = (e.newValue as ThemeMode) || "system";
      setModeState(next);
      setResolvedTheme(next === "system" ? getSystemTheme() : next);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const setMode = useCallback((next: ThemeMode) => {
    setModeState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
    setResolvedTheme(next === "system" ? getSystemTheme() : next);
  }, []);

  const toggle = useCallback(() => {
    setMode(resolvedTheme === "dark" ? "light" : "dark");
  }, [resolvedTheme, setMode]);

  const value = useMemo<ThemeContextValue>(
    () => ({ mode, resolvedTheme, setMode, toggle }),
    [mode, resolvedTheme, setMode, toggle]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = (): ThemeContextValue => {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    /* Defensive fallback so a stray import never crashes the app. */
    return {
      mode: "light",
      resolvedTheme: "light",
      setMode: () => {},
      toggle: () => {},
    };
  }
  return ctx;
};

export default ThemeProvider;
